'use client';

import { useId, type ReactElement } from 'react';
import { CustomImage } from 'components/shared/custom-image';
import { getSpeedsizeImageUrl } from 'components/shared/custom-image/CustomImage';
import styles from './Hero.module.css';

const ASSET = 'web_v2/why-ultrahuman/why-ultrahuman-hero-cards-v2';

export type HeroReason = 'research' | 'accuracy' | 'personal' | 'data';

/* Figma 2095:11583's logo cells on "Loved by Researchers": x, y, white
   opacity, and whether the cell carries a partner mark. */
const LOGO_CELLS: [number, number, number, boolean][] = [
  [-38, 226.55, 0.3, false],
  [121, 226.55, 0.6, true],
  [280, 226.55, 0.6, true],
  [439, 226.55, 0.3, false],
  [-108, 326.55, 0.3, false],
  [51.75, 326.55, 0.6, true],
  [211.5, 326.55, 0.8, true],
  [371.25, 326.55, 0.6, true],
  [531, 326.55, 0.3, false],
  [-38, 426.55, 0.3, false],
  [121, 426.55, 0.6, true],
  [280, 426.55, 0.6, true],
  [439, 426.55, 0.3, false],
];

/* Wide research-partner wordmarks in their brand colours, one per marked
   cell, named as SCIENCE_PARTNER_LOGOS. */
const LOGO_IDS = [
  'pfizer',
  'unsw',
  'johns-hopkins-aramco',
  'lehigh-university',
  'liverpool-john-moores',
  'universitat-leipzig',
  'ivi-rma-global',
];

/* "Make it personal": Figma's PowerPlug circles, x and y of each 104px
   circle's box; the 124px image carries its own shadow. */
const PLUG_CIRCLES: [number, number][] = [
  [166.21, 205.91],
  [306.6, 205.9],
  [96, 327.49],
  [376.8, 327.49],
  [166.2, 449.08],
  [306.61, 449.09],
];

/* Blurred shapes the art fades out through, as Figma's mask groups. */
const SoftMask = ({ id, children }: { id: string; children: ReactElement }) => (
  <>
    <filter id={`${id}-blur`} x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="26.25" />
    </filter>
    <mask
      id={id}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="577"
      height="596"
    >
      <g filter={`url(#${id}-blur)`}>{children}</g>
    </mask>
  </>
);

const ResearchArt = () => {
  const id = useId();
  let next = 0;
  return (
    <svg viewBox="0 0 577 596" aria-hidden="true">
      <defs>
        <SoftMask id={id}>
          <ellipse cx="288.5" cy="371.55" rx="236.69" ry="193.55" fill="#fff" />
        </SoftMask>
      </defs>
      <g mask={`url(#${id})`}>
        {LOGO_CELLS.map(([x, y, alpha, marked]) => {
          const logo = marked ? LOGO_IDS[next++] : undefined;
          return (
            <g key={`${x}-${y}`}>
              <rect
                x={x}
                y={y}
                width="151"
                height="92"
                rx="16"
                fill="#fff"
                fillOpacity={alpha}
              />
              {logo ? (
                <image
                  href={getSpeedsizeImageUrl(
                    `web_v2/why-ultrahuman/why-ultrahuman-partner-logos/${logo}.png`,
                    320,
                  )}
                  x={x + 22}
                  y={y + 28}
                  width="107"
                  height="34"
                  preserveAspectRatio="xMidYMid meet"
                />
              ) : null}
            </g>
          );
        })}
      </g>
    </svg>
  );
};

const PersonalArt = () => {
  const id = useId();
  return (
    <svg className={styles.plugArt} viewBox="0 0 577 596" aria-hidden="true">
      <defs>
        <SoftMask id={id}>
          <circle cx="288.5" cy="379.5" r="162.19" fill="#fff" />
        </SoftMask>
        <clipPath id={`${id}-centre`}>
          <circle cx="288.49" cy="379.46" r="57.5" />
        </clipPath>
        <filter
          id={`${id}-shadow`}
          x="-20%"
          y="-20%"
          width="140%"
          height="150%"
        >
          <feDropShadow dx="0" dy="8" stdDeviation="5" floodOpacity="0.06" />
        </filter>
      </defs>
      <g mask={`url(#${id})`}>
        {PLUG_CIRCLES.map(([x, y], i) => (
          <image
            key={`${x}-${y}`}
            href={getSpeedsizeImageUrl(`${ASSET}/personal-${i + 1}.png`, 248)}
            x={x - 10}
            y={y - 2}
            width="124"
            height="124"
          />
        ))}
        <g filter={`url(#${id}-shadow)`}>
          <image
            href={getSpeedsizeImageUrl(`${ASSET}/personal-center.png`, 460)}
            clipPath={`url(#${id}-centre)`}
            x="191.64"
            y="281.7"
            width="194.87"
            height="194.87"
          />
        </g>
      </g>
    </svg>
  );
};

/* "Your data is yours" (Figma 2261:14677): three layers in the card box, so
   each keeps Figma's blend. A CSS glow adds light (plus-lighter) behind
   the nested glass shields, and the Ring Pro in Aster Black (Ring Pro's
   cart and order image) sits at their centre. */
/* Figma's nested glass shields, largest first: path, its size and opacity.
   Each is tilted as Figma's (-1deg, skew 4deg). */
const OUTER_SHIELD =
  'M0 13.14C0 5.88 5.88 0 13.14 0H495.33C502.58 0 508.47 5.88 508.47 13.14V264.98C508.47 341.82 468.7 413.19 403.36 453.62L261.15 541.62C256.91 544.24 251.56 544.24 247.32 541.62L105.11 453.62C39.76 413.19 0 341.82 0 264.98V13.14Z';
const MID_SHIELD =
  'M0 13.14C0 5.88 5.88 0 13.14 0H348.32C355.58 0 361.46 5.88 361.46 13.14V188.37C361.46 242.99 333.19 293.73 286.74 322.47L187.64 383.79C183.41 386.41 178.05 386.41 173.81 383.79L74.72 322.47C28.27 293.73 0 242.99 0 188.37V13.14Z';
const INNER_SHIELD =
  'M0 13.14C0 5.88 5.88 0 13.14 0H239.99C247.25 0 253.13 5.88 253.13 13.14V131.92C253.13 170.17 233.33 205.7 200.8 225.82L133.48 267.48C129.24 270.11 123.89 270.11 119.65 267.48L52.32 225.82C19.8 205.7 0 170.17 0 131.92V13.14Z';

const CORE_SHIELD =
  'M13.14 0H130.86C137.65 0 143.15 5.5 143.15 12.29V74.19C143.15 95.66 132.04 115.6 113.79 126.89L78.47 148.75C74.51 151.2 69.5 151.2 65.54 148.75L30.22 126.89C11.96 115.6 0.85 95.66 0.85 74.19V12.29C0.85 5.5 6.36 0 13.14 0Z';

/* Figma's four shields share one centre and scale; the outermost runs past
   the card's sides, as designed. */
const SHIELDS: [string, number, number, number][] = [
  [OUTER_SHIELD, 508.47, 543.59, 1],
  [MID_SHIELD, 361.46, 385.76, 0.7],
  [INNER_SHIELD, 253.13, 269.45, 0.4],
  [CORE_SHIELD, 144, 152.29, 0.2],
];
const SHIELD_SCALE = 0.97;

const DataArt = () => {
  const id = useId();
  return (
    <>
      <span className={styles.dataGlow} />
      <svg viewBox="0 0 577 596" aria-hidden="true">
        <defs>
          <SoftMask id={id}>
            <ellipse cx="288.5" cy="397" rx="251" ry="205.5" fill="#fff" />
          </SoftMask>
          <linearGradient
            id={`${id}-sheen`}
            x1="6.35"
            y1="487.36"
            x2="623.13"
            y2="-60.48"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#fff" stopOpacity="0.7" />
            <stop offset="0.38" stopColor="#fff" stopOpacity="0" />
            <stop offset="0.59" stopColor="#fff" stopOpacity="0" />
            <stop offset="1" stopColor="#fff" />
          </linearGradient>
        </defs>
        <g mask={`url(#${id})`}>
          {SHIELDS.map(([d, w, h, opacity], i) => (
            <g
              key={d}
              opacity={opacity}
              transform={`translate(288.5 400) rotate(-1) skewX(4) scale(${SHIELD_SCALE}) translate(${
                -w / 2
              } ${-h / 2})`}
            >
              {i === 0 ? (
                <>
                  <path d={d} fill="#d7c8c1" />
                  <path d={d} fill={`url(#${id}-sheen)`} />
                </>
              ) : (
                <path d={d} fill="#fff" fillOpacity="0.08" />
              )}
              <path
                d={d}
                fill="none"
                stroke="#fff"
                strokeOpacity="0.6"
                strokeWidth={1.8 / SHIELD_SCALE}
              />
            </g>
          ))}
        </g>
      </svg>
      <span className={styles.dataRing}>
        <CustomImage
          src="web_v2/ring-pro/order-tracking/v2/aster-black.png"
          alt=""
          fill
          sizes="(min-width: 992px) 165px, 31vw"
        />
      </span>
    </>
  );
};

/* Each hero card's art, laid out in Figma's 577 x 596 card. Clinical-grade
   accuracy is an export of its Figma art (the list's blur drawn below); the
   others are layered here so blends and live photos keep working. */
export const HeroCardArt = ({ reason }: { reason: HeroReason }) => {
  let art: ReactElement;
  if (reason === 'research') art = <ResearchArt />;
  else if (reason === 'personal') art = <PersonalArt />;
  else if (reason === 'data') art = <DataArt />;
  else {
    art = (
      <>
        <CustomImage
          src="web_v2/why-ultrahuman/accuracy-art-v3.png"
          alt=""
          fill
          sizes="(min-width: 992px) 577px, 100vw"
        />
        <span className={styles.listFade} />
      </>
    );
  }
  return <div className={styles.art}>{art}</div>;
};
